import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Grid,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PersonIcon from '@mui/icons-material/Person';
import CastIcon from "@mui/icons-material/Cast";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import { useI18n } from "../i18n/I18nProvider";
import { supabase } from "../logic/client";


interface Participant {
  id: string;
  name: string;
}

export default function ControlPanel(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const { lang } = useI18n();

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const L = (en: string, de: string) => (lang === "de" ? de : en);

  const [participants, setParticipants] = useState<Participant[]>([]);
  const [workshopTitle, setWorkshopTitle] = useState<string | null>(null);
  const [workshopDate, setWorkshopDate] = useState<string | null>(null);

  useEffect(() => {
    loadWorkshopInfo();
  }, []);

  async function loadWorkshopInfo() {
    try {
      const { data } = await supabase.functions.invoke("get-active-workshop");

      if (data?.workshop) {
        setWorkshopTitle(data.workshop.title);
        setWorkshopDate(data.workshop.workshop_date);
      } else {
        setWorkshopTitle(null);
        setWorkshopDate(null);
      }

      const parsedParticipants: Participant[] =
        data?.participants?.map((p: any) => ({
          id: p.employee_id,
          name:
            [p.employees?.first_name, p.employees?.last_name]
              .filter(Boolean)
              .join(" ") || "Anonymous",
        })) ?? [];

      setParticipants(parsedParticipants);
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
      }}
    >
      <Box
        sx={{
          px: isTablet ? 2 : 4,
          py: isTablet ? 4 : 8,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Stack spacing={3} sx={{ width: "100%", maxWidth: 900 }}>
          {/* HEADER */}
          <Paper sx={glassCard}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              mb={2}
            >
              {/* LEFT SIDE (title + chip) */}
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography fontWeight={800} sx={{ fontSize: { xs: "1.5rem", sm: "1.9rem", md: "2.125rem" }, lineHeight: 1.15 }}>
                  {workshopTitle || L("No active workshop", "Kein aktiver Workshop")}
                </Typography>

                {workshopTitle && (
                  <Chip
                    label="ACTIVE"
                    size="small"
                    sx={{
                      background: "#e40505",
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  />
                )}
              </Box>
            </Box>

            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarTodayOutlinedIcon sx={{ opacity: 0.7 }} />
                <Typography sx={{ opacity: 0.8 }}>
                  {workshopDate
                    ? new Date(workshopDate).toLocaleDateString()
                    : "—"}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <GroupsOutlinedIcon sx={{ opacity: 0.7 }} />
                <Typography sx={{ opacity: 0.8 }}>
                  {participants.length} {L("participants", "Teilnehmer")}
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          {/* APP GRID */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <AppTile
                icon={<GroupsOutlinedIcon />}
                label={L("Manage Workshops", "Workshops Verwalten")}
                onClick={() => handlePageChange("WorkshopManagement")}
              />
            </Grid>

            <Grid item xs={6}>
              <AppTile
                icon={<CastIcon />}
                label={L("Manage Screens", "Steuerungsansichten")}
                onClick={() => handlePageChange("ScreenManagement")}
                disabled
              />
            </Grid>

            <Grid item xs={6}>
              <AppTile
                icon={<AnalyticsOutlinedIcon />}
                label={L("Manager Dashboard", "Manager-Dashboard")}
                onClick={() => handlePageChange("GroupDashboard")}
              />
            </Grid>

            <Grid item xs={6}>
              <AppTile
                icon={<CollectionsOutlinedIcon />}
                label={L("Lab Photos", "Lab-Fotos")}
                onClick={() => handlePageChange("LabPhotos")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <AppTile
                icon={<PersonIcon />}
                label={L("Profile Settings", "Profileinstellungen")}
                onClick={() => handlePageChange("Profile")}
              />
            </Grid>
          </Grid>
        </Stack>
      </Box>
    </Box>
  );
}

/* GLASS STYLE */
const glassCard = {
  borderRadius: { xs: 3, sm: 5 },
  p: { xs: 2.5, sm: 4 },
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
  backdropFilter: "blur(14px)",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "white",
};

/* APP TILE (OS STYLE) */
function AppTile({
  icon,
  label,
  onClick,
  disabled = false,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Paper
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled}
      sx={{
        height: { xs: 112, sm: 140 },
        borderRadius: { xs: 3, sm: 5 },
        cursor: disabled ? "not-allowed" : "pointer",

        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        p: 1.5,
        textAlign: "center",

        background:
          "linear-gradient(135deg, rgba(180,20,30,0.9), rgba(30,10,10,0.95))",

        border: "1px solid rgba(255,255,255,0.08)",
        color: "#fff",
        opacity: disabled ? 0.45 : 1,

        transition: "all .25s ease",

        "&:hover": disabled
          ? {}
          : {
              transform: "scale(1.05)",
              boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
            },
      }}
    >
      <Box sx={{ fontSize: { xs: 30, sm: 36 }, display: "flex" }}>{icon}</Box>

      <Typography fontWeight={700} textAlign="center" sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
        {label}
      </Typography>
    </Paper>
  );
}
